import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRight, Clock, Award } from 'lucide-react';

// Industry color mapping for badges
const industryColors = {
  'manufacturing': 'bg-blue-500 text-white',
  'information-technology': 'bg-purple-500 text-white',
  'aerospace': 'bg-orange-500 text-white',
  'medical-devices': 'bg-green-500 text-white',
  'healthcare': 'bg-teal-500 text-white',
  'corporate-services': 'bg-indigo-500 text-white',
  'supply-chain-logistics': 'bg-amber-500 text-white',
};

export const TrainingCard = ({ training, variants }) => {
  const navigate = useNavigate();
  const TrainingIcon = training.icon;
  const industryColor = industryColors[training.industry?.slug] || 'bg-gray-500 text-white';

  const handleClick = () => {
    navigate(`/industry-training/${training.industry.slug}/${training.slug}`);
  };

  return (
    <motion.div variants={variants}>
      <Card
        className="group h-full hover:shadow-2xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-blue-50/30 cursor-pointer overflow-hidden relative"
        onClick={handleClick}
      >
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

        {/* Header with gradient */}
        <div className="relative h-24 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
          <TrainingIcon className="h-12 w-12 text-white" />
          {/* Industry Badge */}
          <Badge className={`absolute top-3 right-3 border-0 ${industryColor}`}>
            {training.industry?.name}
          </Badge>
          {training.certificationLevels && (
            <Badge className="absolute top-3 left-3 bg-yellow-400 text-gray-900 border-0">
              {training.certificationLevels.length} Levels
            </Badge>
          )}
        </div>

        <CardHeader className="pb-3 relative z-10">
          <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-tight line-clamp-2 mb-3">
            {training.title}
          </CardTitle>
          <p className="text-sm text-gray-600 line-clamp-2">
            {training.shortDescription}
          </p>
        </CardHeader>

        <CardContent className="space-y-4 relative z-10">
          {/* Training details */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Clock className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Duration:</span>
              <span>{training.duration}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Award className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Level:</span>
              <span>{training.level}</span>
            </div>
            {training.deliveryMode && (
              <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
                <span className="font-medium">Mode:</span>
                {training.deliveryMode.slice(0, 2).map((mode, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                  >
                    {mode}
                  </Badge>
                ))}
                {training.deliveryMode.length > 2 && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-gray-50 text-gray-600 border-gray-200"
                  >
                    +{training.deliveryMode.length - 2}
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="pt-4 border-t border-gray-100">
            <Button
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              View Details
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
